<template>
	<v-container fluid class="pa-0" style="position: relative">
		<v-overlay
			:active="loading"
			absolute
			class="d-flex align-center justify-center"
		>
			<v-progress-circular indeterminate size="64" color="primary" />
		</v-overlay>

		<v-raw-data-viewer
			v-model="showViewer"
			:raw-data="rawData"
			:file-names="fileNames"
			:module-window="moduleWindow"
			standalone
			@update-json="updateJson"
			@reload-data="loadSdeData"
		/>
	</v-container>
</template>

<script>
import VRawDataViewer from "@/pages/RawDataViewer/VRawDataViewer.vue";
import { APP_NAME } from "@/constants";

export default {
	name: "RawDataViewerPage",

	components: {
		VRawDataViewer,
	},

	data() {
		return {
			rawData: {},
			fileNames: [],
			moduleWindow: null,
			showViewer: true,
			loading: false,
			paperName: null,
		};
	},

	mounted() {
		// load data from server
		this.loadSdeData();

		// set initial title
		this.updatePageTitle();
	},

	methods: {
		updatePageTitle() {
			document.title = `${APP_NAME} - Raw Data Viewer`;
		},

		extractAuthorAndYear(paperName) {
			if (!paperName) {
				return null;
			}

			// get the part before the first semicolon
			const beforeSemicolon = paperName.split(";")[0].trim();
			if (!beforeSemicolon) {
				return null;
			}

			// look for a year (4 digits) in this part
			const yearMatch = beforeSemicolon.match(/\b(19|20)\d{2}\b/);
			const year = yearMatch ? yearMatch[0] : null;

			// remove the year from the part and clean up
			let authorPart = beforeSemicolon.replace(/\b(19|20)\d{2}\b/, "").trim();

			// if we have a year but no author part, try to get author from the original
			if (year && !authorPart) {
				// look for author patterns (usually last name is the last word before the year)
				const beforeYear = beforeSemicolon
					.substring(0, beforeSemicolon.indexOf(year))
					.trim();
				const words = beforeYear.split(/\s+/);
				if (words.length > 0) {
					authorPart = words[words.length - 1]; // take the last word as the last name
				}
			}

			// if we still don't have an author, just take the first few words
			if (!authorPart && beforeSemicolon) {
				const words = beforeSemicolon.split(/\s+/);
				authorPart = words.slice(0, 2).join(" "); // take first two words
			}

			// clean up the author part by removing commas and extra whitespace
			if (authorPart) {
				authorPart = authorPart.replace(/[,]+/g, "").trim();
			}

			if (authorPart && year) {
				return `${authorPart}, ${year}`;
			} else if (authorPart) {
				return authorPart;
			} else if (year) {
				return year;
			}

			return null;
		},

		loadSdeData() {
			const urlParams = new URLSearchParams(window.location.search);
			const uuid = urlParams.get("uuid");

			if (!uuid) {
				console.error("No UUID provided in URL parameters");
				return;
			}

			this.loading = true;
			this.$http
				.get(`/data-entry/${uuid}/raw-data-viewer`)
				.then((response) => {
					this.rawData = response.data.rawData || {};
					this.fileNames = response.data.fileNames || [];
					this.paperName = response.data.paperName || null;

					// update title with more specific information if available
					this.updateTitleWithData();
				})
				.catch((error) => {
					console.error("Error loading raw data viewer data:", error);
					if (error.response?.data?.message) {
						console.error("Server error:", error.response.data.message);
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},

		updateTitleWithData() {
			const authorAndYear = this.extractAuthorAndYear(this.paperName);

			if (authorAndYear) {
				document.title = `${APP_NAME} - Raw Data Viewer (${authorAndYear})`;
			} else {
				// fallback to basic title
				this.updatePageTitle();
			}
		},

		updateJson(json) {
			// handle JSON updates if needed
			console.log("JSON updated:", json);
		},
	},
};
</script>

<style scoped>
/* Add any page-specific styles here */
</style>
